import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { resendSignUp } from "data/mutations-common";
import { Form, Formik, FormikConfig } from "formik";
import { useMutation } from "@tanstack/react-query";
import * as Routes from "routes";
import { InferType, object, string } from "yup";

export const ResendVerificationValidationSchema = object({
  email: string().email("Enter a valid email").required("Email is required"),
});

interface ResendVerificationValues {
  email: string;
}

export const ResendVerification = () => {
  const resendVerificationMutation = useMutation({
    mutationFn: resendSignUp,
  });

  const onSubmit: FormikConfig<
    InferType<typeof ResendVerificationValidationSchema>
  >["onSubmit"] = async ({ email }) => {
    resendVerificationMutation.mutate(email);
  };

  return (
    <Formik<ResendVerificationValues>
      initialValues={{ email: "" }}
      onSubmit={onSubmit}
      validationSchema={ResendVerificationValidationSchema}
    >
      {({ values: { email }, handleChange, handleBlur, errors, touched }) => (
        <Form>
          <div className="flex flex-col items-center min-w-[100vw]">
            <Card className="min-w-[340px] max-w-[500px] m-2">
              <CardHeader>
                <CardTitle className="text-xl">Resend Verification</CardTitle>
                <CardDescription>
                  If you have lost your verification link, we can send another.
                </CardDescription>
              </CardHeader>

              <CardContent className="flex flex-col gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="text"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={resendVerificationMutation.isPending}
                    className={
                      touched.email && errors.email
                        ? "border-destructive"
                        : ""
                    }
                  />
                  {touched.email && errors.email && (
                    <p className="text-xs text-destructive">{errors.email}</p>
                  )}
                </div>

                {resendVerificationMutation.isPending ? (
                  <div className="flex justify-center w-full py-2">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    {resendVerificationMutation.isSuccess && (
                      <Alert>
                        <AlertDescription>
                          Verification link has been successfully resent to{" "}
                          {email}.
                        </AlertDescription>
                      </Alert>
                    )}

                    {resendVerificationMutation.isError && (
                      <Alert variant="destructive">
                        <AlertDescription>
                          Could not resend confirmation.
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      disabled={resendVerificationMutation.isPending}
                      className="w-full"
                    >
                      Resend
                    </Button>

                    <div className="flex justify-center gap-1">
                      <Button variant="link" size="sm" asChild>
                        <a href={Routes.signUp}>Sign up</a>
                      </Button>
                      <Button variant="link" size="sm" asChild>
                        <a href={Routes.signIn}>Sign In</a>
                      </Button>
                      <Button variant="link" size="sm" asChild>
                        <a href={Routes.resetPassword}>Password Reset</a>
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </Form>
      )}
    </Formik>
  );
};
